import classNames from 'classnames'
import Icon from 'common/Icon'
import { CRISIS_LIFELINE_NUMBER } from 'constants/common'
import {
  footerLegalLinks,
  insuranceLinks,
  resourcesLinks,
  servicesLinks,
  socialLinks,
  thriveworksLinks,
} from 'constants/footer'
import useBreakpoint from 'hooks/useBreakPoint'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef } from 'react'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import { isServer } from 'utils/environment'
import CookiePreferencesLink from './components/CookiePreferencesLink'
import FooterTrustBadges from './components/FooterTrustBadges'
import FooterValueProp from './components/FooterValueProp'
import Links from './components/Links'
import MobileAccordionLinks from './components/MobileAccordionLinks'
import NewsletterSignup from './components/NewsletterSignup'
import { isSeFriendlyRoute } from 'app/SeFriendly/helpers'
import useFooterLinkUrl from './hooks/useFooterLinkUrl'
import styles from './index.module.scss'

type FooterType = {
  hasExtendedBottom?: boolean
  onFooterVisibility?: (isFooterVisible: boolean) => void
}

const Footer: React.FC<FooterType> = ({
  hasExtendedBottom = false,
  onFooterVisibility,
}) => {
  const router = useRouter()
  const isSeFriendly = isSeFriendlyRoute(router.pathname, router.asPath)
  const footerRef = useRef()
  const observer = useMemo(
    () =>
      !isServer()
        ? new IntersectionObserver(([entry]) =>
            onFooterVisibility(entry.isIntersecting)
          )
        : null,
    [onFooterVisibility]
  )

  useEffect(() => {
    if (onFooterVisibility && observer) {
      observer.observe(footerRef.current)
    }

    return () => {
      if (onFooterVisibility && observer) {
        observer.disconnect()
      }
    }
  }, [])

  const { isMobileScreen } = useBreakpoint()

  const enhanceFooterUrl = useFooterLinkUrl()

  const footerLinks = useMemo(() => {
    const footerLinkGroups = {
      thriveworksLinks,
      resourcesLinks,
      servicesLinks,
      insuranceLinks,
    }

    return Object.fromEntries(
      Object.entries(footerLinkGroups).map(([key, links]) => [
        key,
        links.map(({ link, ...rest }) => ({
          ...rest,
          link: enhanceFooterUrl(link),
        })),
      ])
    ) as typeof footerLinkGroups
  }, [enhanceFooterUrl])

  return (
    <footer className={styles.footer_wrapper}>
      <Container fluid className={`px-0 px-md-3 ${styles.footer_container}`}>
        <FooterValueProp />
        {/* Footer Links Section */}
        <Row
          ref={footerRef}
          className={`pb-5 pb-md-0 app_max-width ${styles.footer_side_padding}`}
        >
          <Col lg={12}>
            {isMobileScreen ? (
              <MobileAccordionLinks
                {...footerLinks}
                isSeFriendly={isSeFriendly}
              />
            ) : (
              <Links {...footerLinks} isSeFriendly={isSeFriendly} />
            )}
          </Col>
        </Row>
      </Container>
      <div
        className={classNames(
          `container-fluid ${styles.footer_container} ${styles.footer_top_margin}`,
          {
            [styles.footer_extended_bottom]: hasExtendedBottom,
          }
        )}
      >
        {/* Desktop - Crisis message */}
        {!isMobileScreen && (
          <Row
            className={`app_max-width ${styles.footer_side_padding} ${styles.desktop_immediate_help_message}`}
          >
            <div className={`mb-3 ${styles.immediate_help_message}`}>
              In crisis or need immediate help?
              <span className="d-block font-weight-normal">
                Call{' '}
                <a
                  href={`tel:${CRISIS_LIFELINE_NUMBER}`}
                  className={styles.crisis_link}
                >
                  {CRISIS_LIFELINE_NUMBER}
                </a>{' '}
                or go to your nearest ER.
              </span>
            </div>
          </Row>
        )}

        {/* Horizontal line separator */}
        <Row className={`app_max-width ${styles.footer_side_padding}`}>
          <Col lg={12} className="px-0">
            <hr className={styles.footer_horizontal_line} />
          </Col>
        </Row>

        {/* Mobile - Crisis message */}
        {isMobileScreen && (
          <Row className={`app_max-width ${styles.footer_side_padding}`}>
            <Col lg={12} className="px-0">
              <div className={`py-2 ${styles.immediate_help_message}`}>
                In crisis or need immediate help?
                <span className="d-block font-weight-normal">
                  Call{' '}
                  <a
                    href={`tel:${CRISIS_LIFELINE_NUMBER}`}
                    className={styles.crisis_link}
                  >
                    {CRISIS_LIFELINE_NUMBER}
                  </a>{' '}
                  or go to your nearest ER.
                </span>
              </div>
            </Col>
          </Row>
        )}

        {/* Logo + Newsletter Row */}
        <Row className={`app_max-width ${styles.footer_side_padding} py-4`}>
          <Col lg={12} className="px-0">
            <div className={styles.logo_newsletter_container}>
              {/* Logo - separate from newsletter */}
              <div className={styles.footer_logo_wrapper}>
                <Image
                  src="/images/footer-logo.svg"
                  height={48}
                  width={180}
                  className={styles.thriveworks_footer_logo}
                  alt="Thriveworks logo"
                />
              </div>
              {/* Newsletter */}
              <NewsletterSignup />
            </div>
          </Col>
        </Row>

        {/* Social Media Icons - Centered */}
        <Row className={`app_max-width ${styles.footer_side_padding}`}>
          <Col lg={12} className="px-0">
            <ul className={styles.socialmedia_centered}>
              {socialLinks.map((icon) => (
                <li
                  className={styles.socialmedia_item}
                  key={`${icon.link + icon.icon}`}
                >
                  <Link
                    href={icon.link}
                    passHref
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none"
                    aria-label={icon.icon}
                  >
                    <Icon
                      icon={icon.icon}
                      fill="white"
                      className={`fa-lg ${styles.socialmedia_icons}`}
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </Col>
        </Row>

        {/* Bottom Section */}
        <Row className={`app_max-width ${styles.footer_side_padding}`}>
          <Col lg={12} className="px-0">
            <div className={styles.footer_bottom_section}>
              <div className={styles.footer_bottom_left}>
                <p className={styles.footer_bottom_text}>
                  All copyrights for logos are owned by their respective
                  companies.
                </p>
                <p className={styles.footer_bottom_text}>
                  &copy; {new Date().getFullYear()} Thriveworks Administrative
                  Services, LLC
                </p>
              </div>

              {/* Session availability */}
              <div className={styles.footer_bottom_middle}>
                <p className={styles.footer_bottom_text}>
                  Our sessions are subject to availability. Check back often.
                </p>
              </div>

              {/* Privacy / legal links */}
              <div className={styles.footer_bottom_right}>
                <div className={styles.footer_privacy_links}>
                  {footerLegalLinks.map((legalLink) => {
                    if (legalLink.type === 'action') {
                      return (
                        <CookiePreferencesLink
                          key={legalLink.label}
                          className={styles.footer_link}
                        />
                      )
                    }

                    return (
                      <Link
                        key={legalLink.label}
                        href={enhanceFooterUrl(legalLink.href)}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.footer_link}
                      >
                        {legalLink.label}
                      </Link>
                    )
                  })}
                </div>
              </div>

              <div className={styles.footer_bottom_badges}>
                <FooterTrustBadges />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </footer>
  )
}

export default Footer
